import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RotateCcw, AlertTriangle, FileWarning, ChevronDown, ChevronRight, Download, Search, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseReturnsPage() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dnFilter, setDnFilter] = useState('all');
  const [varianceFilter, setVarianceFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);

  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/purchase-returns`, { headers: getAuthHeader() });
      setData(res.data.data || []);
      setSummary(res.data.summary || {});
    } catch (e) {
      toast.error('Failed to load purchase returns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const formatCurrency = (v) => `₹${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';

  const filtered = data.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (dnFilter === 'missing' && !r.is_dn_missing) return false;
    if (dnFilter === 'draft' && r.dn_status !== 'draft') return false;
    if (dnFilter === 'submitted' && r.dn_status !== 'submitted') return false;
    if (dnFilter === 'approved' && r.dn_status !== 'approved') return false;
    if (varianceFilter === 'with' && !r.is_variance) return false;
    if (varianceFilter === 'without' && r.is_variance) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (r.po_number || '').toLowerCase().includes(s) ||
             (r.supplier_name || '').toLowerCase().includes(s) ||
             (r.demand_number || '').toLowerCase().includes(s) ||
             (r.dc_number || '').toLowerCase().includes(s);
    }
    return true;
  });

  const exportCSV = () => {
    const headers = ['Date','Return No','PO','Supplier','Items','Return Value','Status','DC','GP Status','DN','DN Status','DN Amount','Variance','Variance %'];
    const rows = filtered.map(r => [
      formatDate(r.created_at), r.demand_number, r.po_number, r.supplier_name, r.items_count,
      r.return_value, r.status, r.dc_number || '', r.gp_status || '', r.dn_number || (r.is_dn_missing ? 'MISSING' : ''),
      r.dn_status || '', r.dn_value, r.variance, r.variance_pct
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `purchase-returns-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const statusBadge = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-800', released: 'bg-blue-100 text-blue-800', dispatched: 'bg-green-100 text-green-800' };
    return <Badge className={`${map[s] || 'bg-gray-100 text-gray-800'} text-xs`}>{s}</Badge>;
  };

  const gpBadge = (s) => {
    if (!s) return <span className="text-xs text-muted-foreground">-</span>;
    const map = { draft: 'bg-gray-100 text-gray-700', pending_out: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', completed: 'bg-green-100 text-green-700' };
    return <Badge className={`${map[s] || ''} text-xs`}>{s === 'approved' ? 'Out' : s}</Badge>;
  };

  const dnBadge = (s, missing) => {
    if (missing) return <Badge className="bg-red-100 text-red-700 text-xs">Missing</Badge>;
    if (!s) return <span className="text-xs text-muted-foreground">-</span>;
    const map = { draft: 'bg-gray-100 text-gray-700', submitted: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700', sent: 'bg-emerald-100 text-emerald-700', acknowledged: 'bg-emerald-100 text-emerald-700' };
    return <Badge className={`${map[s] || ''} text-xs`}>{s}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="purchase-returns-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Purchase Returns Register</h1>
            <p className="text-muted-foreground">Track material returns to suppliers with DN audit</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><RotateCcw className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-xs text-muted-foreground">Total Returns</p><p className="text-xl font-bold">{summary.total_returns || 0}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg"><RotateCcw className="w-5 h-5 text-yellow-600" /></div>
            <div><p className="text-xs text-muted-foreground">Pending Release</p><p className="text-xl font-bold text-yellow-600">{summary.pending_release || 0}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg"><FileWarning className="w-5 h-5 text-red-600" /></div>
            <div><p className="text-xs text-muted-foreground">DN Missing</p><p className="text-xl font-bold text-red-600">{summary.dn_missing || 0}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-xs text-muted-foreground">Amount Variance</p><p className="text-xl font-bold text-amber-600">{summary.variance_count || 0}</p></div>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search PO, supplier, return no..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
          </div>
          <div className="w-36">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={dnFilter} onValueChange={setDnFilter}>
              <SelectTrigger><SelectValue placeholder="DN Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All DN</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Select value={varianceFilter} onValueChange={setVarianceFilter}>
              <SelectTrigger><SelectValue placeholder="Variance" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="with">With Variance</SelectItem>
                <SelectItem value="without">No Variance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>PO / Supplier</TableHead>
                  <TableHead className="text-right">Return Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>DC</TableHead>
                  <TableHead>GP</TableHead>
                  <TableHead>DN</TableHead>
                  <TableHead>DN Status</TableHead>
                  <TableHead className="text-right">DN Amount</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No purchase returns found</TableCell></TableRow>
                ) : filtered.map(r => (
                  <React.Fragment key={r.demand_id}>
                    <TableRow
                      className={`cursor-pointer hover:bg-muted/50 ${r.is_dn_missing ? 'bg-red-50' : r.is_variance ? 'bg-amber-50' : ''}`}
                      onClick={() => setExpandedRow(expandedRow === r.demand_id ? null : r.demand_id)}
                      data-testid={`return-row-${r.demand_id}`}
                    >
                      <TableCell>
                        {expandedRow === r.demand_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(r.created_at)}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{r.po_number}</div>
                        <div className="text-xs text-muted-foreground">{r.supplier_name}</div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(r.return_value)}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell><span className="text-xs font-mono">{r.dc_number || '-'}</span></TableCell>
                      <TableCell>{gpBadge(r.gp_status)}</TableCell>
                      <TableCell>
                        {r.is_dn_missing
                          ? <Badge className="bg-red-100 text-red-700 text-xs">Missing</Badge>
                          : <span className="text-xs font-mono">{r.dn_number || '-'}</span>}
                      </TableCell>
                      <TableCell>{dnBadge(r.dn_status, r.is_dn_missing)}</TableCell>
                      <TableCell className="text-right">{r.dn_value ? formatCurrency(r.dn_value) : '-'}</TableCell>
                      <TableCell className="text-right">
                        {r.dn_number ? (
                          <span className={`font-medium ${r.variance > 0 ? 'text-red-600' : r.variance < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {r.variance === 0 ? '-' : `${formatCurrency(Math.abs(r.variance))} (${Math.abs(r.variance_pct)}%)`}
                          </span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>

                    {expandedRow === r.demand_id && (
                      <TableRow>
                        <TableCell colSpan={11} className="bg-muted/30 p-0">
                          <div className="p-4">
                            <div className="text-xs text-muted-foreground mb-2">
                              Requested by: {r.requested_by_name} | {r.items_count} item(s)
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Item</TableHead>
                                  <TableHead>Reason</TableHead>
                                  <TableHead className="text-right">Qty to Return</TableHead>
                                  <TableHead className="text-right">Qty Released</TableHead>
                                  <TableHead className="text-right">Unit Price</TableHead>
                                  <TableHead className="text-right">Return Value</TableHead>
                                  <TableHead className="text-right">DN Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {r.items.map((it, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{it.item_name}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{it.reason || '-'}</TableCell>
                                    <TableCell className="text-right">{it.qty_to_return}</TableCell>
                                    <TableCell className="text-right">{it.qty_released}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(it.unit_price)}</TableCell>
                                    <TableCell className="text-right font-medium">{formatCurrency(it.return_value)}</TableCell>
                                    <TableCell className="text-right">{it.dn_amount ? formatCurrency(it.dn_amount) : '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
